import { useEffect, useRef } from 'react'
import { CALENDLY_ORIGIN } from '@src/functions/calendly'

type CalendlyHandler = (event: string, payload: any) => void

/**
 * Subscribes to the inline widget's `calendly.*` postMessage events.
 *
 * The handler is held in a ref rather than listed as a dependency so that passing an
 * inline arrow function — the normal way to call this — does not tear the listener
 * down and rebuild it on every render, which can drop a message that lands in between.
 */
export default function useCalendlyMessages(handler: CalendlyHandler) {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== CALENDLY_ORIGIN) return

      const { data } = event
      if (!data || typeof data.event !== 'string' || !data.event.startsWith('calendly.')) return

      handlerRef.current(data.event, data.payload)
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])
}
